"use client";

import React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, ChevronRight, FileText, FlaskConical, Layers, ShieldCheck } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const features = [{
  title: "Pharmacopoeial Alignment",
  description: "Designed to support USP, Ph. Eur., and ISO powder characterisation methods.",
  icon: CheckCircle2
}, {
  title: "Precision Engineering",
  description: "High-grade 316 stainless steel and precision-machined funnels, cups, and dies.",
  icon: Layers
}, {
  title: "Traceable Compliance",
  description: "Comprehensive material certificates and dimensional calibration documentation.",
  icon: ShieldCheck
}];
export default function PowderHero() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };
  return <section id="powdertestingaccessorieshero" data-nav="light" className="relative overflow-hidden bg-background py-16 md:py-24 border-b border-border">
      {/* Decorative background grid subtle overlay */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,var(--color-secondary)_0%,transparent_60%)] pointer-events-none opacity-70" />

      <div className="page-container relative z-10">
        {/* Breadcrumb Navigation */}
        <nav aria-label="Breadcrumb" className="mb-6">
          <ol className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <li>
              <Link to="/HomePage" className="hover:text-foreground transition-colors">
                Home
              </Link>
            </li>
            <li>
              <ChevronRight className="size-3.5" />
            </li>
            <li>
              <Link to="/PowderTestingPage" className="hover:text-foreground transition-colors">
                Powder Testing
              </Link>
            </li>
            <li>
              <ChevronRight className="size-3.5" />
            </li>
            <li className="font-medium text-foreground" aria-current="page">
              Accessories
            </li>
          </ol>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* Left Column: Content */}
          <motion.div className="lg:col-span-7 space-y-6" variants={containerVariants} initial="hidden" animate="visible">
            <motion.div variants={itemVariants}>
              <Badge variant="secondary" className="px-3 py-1 text-xs gap-1.5 font-medium border border-border">
                <FlaskConical className="size-3.5 text-primary" />
                Laboratory & Process Tooling
              </Badge>
            </motion.div>

            <motion.div variants={itemVariants} className="space-y-4">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight">
                Powder Testing Accessories & Tooling
              </h1>
              <p className="text-base sm:text-lg text-muted-foreground font-sans max-w-2xl leading-relaxed">
                Standardised funnels, flow cylinders, tapped density cylinders, and calibration gauges for regulated pharmaceutical, chemical, and bulk material laboratories across Ireland and the UK.
              </p>
            </motion.div>

            {/* CTA Buttons */}
            <motion.div variants={itemVariants} className="flex flex-wrap gap-3 pt-2">
              <Button asChild size="lg" className="shadow-sm">
                <Link to="/DiscussYourApplicationPage">
                  Discuss Application
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/BookADemoPage">
                  Request Demonstration
                </Link>
              </Button>
            </motion.div>

            {/* Quick Specs / Trust Items */}
            <motion.div variants={itemVariants} className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-6 border-t border-border">
              {features.map((feature, i) => {
              const IconComponent = feature.icon;
              return <div key={feature.title} data-index={i} className="flex flex-col space-y-1">
                    <div className="flex items-center gap-2">
                      <IconComponent className="size-4 text-primary shrink-0" />
                      <span className="text-xs font-semibold text-foreground">{feature.title}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-normal">
                      {feature.description}
                    </p>
                  </div>;
            })}
            </motion.div>
          </motion.div>

          {/* Right Column: Product Imagery Card */}
          <motion.div className="lg:col-span-5" initial={{
          opacity: 0,
          scale: shouldReduceMotion ? 1 : 0.98
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          ease: "easeOut",
          delay: 0.2
        }}>
            <div className="relative rounded-xl border border-border bg-card p-4 sm:p-6 shadow-md overflow-hidden">
              <div className="relative aspect-4/3 w-full rounded-lg overflow-hidden bg-secondary flex items-center justify-center">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/powder_testing_accessories_hero.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/powder_testing_accessories_hero.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/powder_testing_accessories_hero.png-wvc-sizes" alt="Standardised powder testing accessories and tooling components" loading="eager" decoding="async" className="w-full h-full object-cover" />
                <div className="absolute top-3 right-3">
                  <span className="inline-flex items-center gap-1.5 rounded-md bg-background/90 backdrop-blur-xs px-2.5 py-1 text-2xs font-medium text-foreground border border-border">
                    <FileText className="size-3 text-primary" />
                    USP & Ph. Eur.
                  </span>
                </div>
              </div>

              {/* Product Card Footer Details */}
              <div className="mt-4 pt-3 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                <span>Material: 316 Stainless Steel</span>
                <span className="font-medium text-foreground">Traceable Calibration</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}